import { normalizeTimestampToMs } from '../time/normalizeTimestampToMs';
function readExecutionList(tradeLike: any): any[] {
  if (Array.isArray(tradeLike?.executionsList)) return tradeLike.executionsList;
  if (Array.isArray(tradeLike?.executions)) return tradeLike.executions;
  return [];
}

function scopeValue(exec: any, trade: any, aliases: string[]): string {
  for (const source of [exec, trade]) {
    for (const alias of aliases) {
      const value = source?.[alias];
      if (value != null && String(value).trim()) {
        const normalized = String(value).trim().toLowerCase();
        if (aliases.includes('sourceBroker') && normalized === 'auto') continue;
        if (aliases.includes('accountId') && normalized === 'default') continue;
        return normalized;
      }
    }
  }
  return '';
}

export function buildExecutionDedupeKey(exec: any, trade?: any): string {
  const symbol = String(exec?.symbol ?? trade?.symbol ?? '').trim().toUpperCase();
  const side = String(exec?.side ?? exec?.direction ?? exec?.action ?? exec?.type ?? '').trim().toLowerCase();
  const qty = Number(exec?.qty ?? exec?.quantity ?? exec?.size ?? exec?.volume ?? exec?.shares ?? 0);
  const price = Number(exec?.price ?? exec?.fillPrice ?? exec?.avgPrice ?? 0);
  const assetClass = String(exec?.assetClass ?? trade?.assetClass ?? '').trim().toUpperCase();
  const orderId = String(exec?.orderId ?? exec?.order_id ?? exec?.clientOrderId ?? exec?.client_order_id ?? '').trim().toLowerCase();
  const rawId = String(exec?.executionId ?? exec?.execId ?? exec?.id ?? '').trim();
  const baseId = rawId.replace(/(?:#\d+)+$/, '');
  const executionId = /^exec-\d+-\d{10,}$/i.test(baseId) ? '' : baseId.toLowerCase();
  const timestampInput = exec?.timestamp ?? exec?.time ?? exec?.t ?? exec?.ts ?? exec?.executedAt;
  const timestamp = normalizeTimestampToMs(timestampInput) ?? 0;
  // A saved account is the import boundary. Provider account numbers can be
  // shared by multiple saved accounts, so prefer this stable identity.
  const savedAccountScope = (() => {
    for (const aliases of [['sourceAccountRef', 'source_account_ref'], ['sourceAccountId', 'source_account_id']]) {
      for (const source of [exec, trade]) {
        for (const alias of aliases) {
          const value = String(source?.[alias] ?? '').trim();
          if (value) return value.toLowerCase();
        }
      }
    }
    return '';
  })();

  return JSON.stringify([
    scopeValue(exec, trade, ['tenantId', 'tenant_id', 'workspaceId', 'workspace_id']),
    scopeValue(exec, trade, ['userId', 'user_id']),
    savedAccountScope || scopeValue(exec, trade, ['accountId', 'account_id', 'providerAccountId', 'provider_account_id', 'accountNumber', 'account_number', 'sourceAccountRef', 'source_account_ref', 'account', 'accountTag', 'account_tag']),
    scopeValue(exec, trade, ['sourceBroker', 'source_broker', 'broker', 'brokerId', 'broker_id']),
    symbol, side, Number.isFinite(qty) ? qty : 0, Number.isFinite(price) ? price : 0,
    assetClass, orderId, executionId, timestamp, Boolean(trade?.isReversal),
  ]);
}

export function collectExecutionDedupeKeys(tradeLike: any): string[] {
  return readExecutionList(tradeLike).map((exec) => buildExecutionDedupeKey(exec, tradeLike));
}

export function collectExistingExecutionKeys(records: any[]): Set<string> {
  const existingKeys = new Set<string>();
  for (const record of Array.isArray(records) ? records : []) {
    for (const key of collectExecutionDedupeKeys(record)) existingKeys.add(key);
  }
  return existingKeys;
}

/** Keep fill multiplicity: two identical rows without broker IDs are two fills. */
export function collectExistingExecutionKeyCounts(records: any[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const record of Array.isArray(records) ? records : []) {
    for (const key of collectExecutionDedupeKeys(record)) counts.set(key, (counts.get(key) || 0) + 1);
  }
  return counts;
}

export function collectExistingExecutionKeyOccurrences(records: any[]): string[] {
  return Array.from(collectExistingExecutionKeyCounts(records)).flatMap(([key, count]) => Array(count).fill(key));
}

export type ImportOverlapDiagnostic = {
  code: 'PARTIAL_EXECUTION_OVERLAP_REQUIRES_REVIEW' | 'OPEN_POSITION_CONTINUATION_REQUIRES_REVIEW';
  accepted: false;
  symbol: string;
  assetClass?: string;
  executionCount: number;
  duplicateExecutionCount: number;
  rowOrdinals: number[];
  sourceOrderIds?: string[];
  reasons: string[];
};

function getKnownAccountIdentity(record: any): { kind: string; value: string } | null {
  for (const [kind, keys] of [
    ['account_id', ['accountId', 'account_id', 'providerAccountId', 'provider_account_id', 'accountNumber', 'account_number']],
    ['source_account_ref', ['sourceAccountRef', 'source_account_ref']],
    ['account', ['account', 'accountTag', 'account_tag']],
  ] as const) {
    for (const key of keys) {
      const value = String(record?.[key] ?? '').trim();
      if (value && value.toLowerCase() !== 'default') return { kind, value: value.toLowerCase() };
    }
  }
  return null;
}

function getBrokerIdentity(record: any): string {
  const broker = String(record?.sourceBroker ?? record?.source_broker ?? record?.broker ?? record?.brokerId ?? '').trim().toLowerCase();
  return broker === 'auto' || broker === 'unknown' ? '' : broker;
}

function getAssetClass(record: any): string {
  const value = String(record?.assetClass ?? record?.asset_class ?? '').trim().toUpperCase();
  if (['STK', 'STOCK', 'STOCKS', 'EQUITY', 'EQUITIES', 'SHARE', 'SHARES'].includes(value)) return 'STK';
  if (['FUT', 'FUTURE', 'FUTURES'].includes(value)) return 'FUT';
  if (['CASH', 'FOREX', 'FX'].includes(value)) return 'CASH';
  if (['CRYPTO', 'CRYPTOCURRENCY'].includes(value)) return 'CRYPTO';
  return value;
}

function signedExecutionQuantity(execution: any): number {
  const side = String(execution?.side ?? execution?.direction ?? '').trim().toLowerCase();
  const quantity = Math.abs(Number(execution?.qty ?? execution?.quantity ?? execution?.size ?? 0));
  if (!Number.isFinite(quantity)) return 0;
  if (['buy', 'cover', 'buy_to_cover', 'bot'].includes(side)) return quantity;
  if (['sell', 'short', 'sell_short', 'sld'].includes(side)) return -quantity;
  return 0;
}

function isOpenLifecycle(record: any): boolean {
  if (String(record?.status ?? '').trim().toLowerCase() === 'open') return true;
  const executions = readExecutionList(record);
  if (executions.length) {
    const netQuantity = executions.reduce((sum, execution) => sum + signedExecutionQuantity(execution), 0);
    return Math.abs(netQuantity) > 1e-9;
  }
  return record?.closeTimestampMs == null && record?.closeTime == null && record?.exitPrice == null;
}

function scopesAreDefinitelyDifferent(left: any, right: any): boolean {
  const leftBroker = getBrokerIdentity(left);
  const rightBroker = getBrokerIdentity(right);
  if (leftBroker && rightBroker && leftBroker !== rightBroker) return true;

  const leftAccount = getKnownAccountIdentity(left);
  const rightAccount = getKnownAccountIdentity(right);
  return Boolean(leftBroker && rightBroker && leftBroker === rightBroker
    && leftAccount && rightAccount
    && leftAccount.kind === rightAccount.kind
    && leftAccount.value !== rightAccount.value);
}

function mayBeSameInstrument(left: any, right: any): boolean {
  const leftSymbol = String(left?.symbol ?? '').trim().toUpperCase();
  const rightSymbol = String(right?.symbol ?? '').trim().toUpperCase();
  const leftAsset = getAssetClass(left);
  const rightAsset = getAssetClass(right);
  return Boolean(leftSymbol && rightSymbol && leftSymbol === rightSymbol
    && !(leftAsset && rightAsset && leftAsset !== rightAsset));
}

function rowOrdinals(executions: any[]): number[] {
  return [...new Set(executions.flatMap((execution) => {
    const ordinal = Number(execution?.metadata?.orderingEvidence?.sourceRowOrdinal ?? execution?.sourceRowOrdinal);
    return Number.isInteger(ordinal) && ordinal >= 0 ? [ordinal + 1] : [];
  }))].sort((a, b) => a - b);
}

function buildOverlapDiagnostic(
  record: any,
  executions: any[],
  duplicateExecutionCount: number,
  code: ImportOverlapDiagnostic['code'],
  reason: string,
): ImportOverlapDiagnostic {
  return {
    code,
    accepted: false,
    symbol: String(record?.symbol ?? executions[0]?.symbol ?? ''),
    assetClass: getAssetClass(record) || undefined,
    executionCount: executions.length,
    duplicateExecutionCount,
    rowOrdinals: rowOrdinals(executions),
    sourceOrderIds: [...new Set(executions.map((execution) => String(execution?.orderId ?? execution?.order_id ?? '').trim()).filter(Boolean))],
    reasons: [reason],
  };
}

// Without cross-batch reconstruction, any partial overlap or continuation of
// an existing open position is ambiguous. Reject that lifecycle as a unit.
export function filterAndRebuildImportedTrades(
  record: any,
  existingKeys: Set<string> | Map<string, number>,
  diagnostics: ImportOverlapDiagnostic[] = [],
  existingRecords: any[] = [],
): any[] {
  const executions = readExecutionList(record);
  if (!executions.length) return [record];

  const keys = executions.map((execution) => buildExecutionDedupeKey(execution, record));
  const duplicateFlags = keys.map((key) => {
    if (existingKeys instanceof Map) {
      const remaining = existingKeys.get(key) || 0;
      if (remaining <= 0) return false;
      existingKeys.set(key, remaining - 1);
      return true;
    }
    return existingKeys.has(key);
  });
  const duplicateCount = duplicateFlags.filter(Boolean).length;
  if (duplicateCount === executions.length) return [];
  if (duplicateCount > 0) {
    diagnostics.push(buildOverlapDiagnostic(
      record,
      executions,
      duplicateCount,
      'PARTIAL_EXECUTION_OVERLAP_REQUIRES_REVIEW',
      'This lifecycle contains executions already imported. The lifecycle was not saved because it cannot be reconciled safely with the existing trade.',
    ));
    keys.forEach((key) => existingKeys instanceof Map
      ? existingKeys.set(key, (existingKeys.get(key) || 0) + 1)
      : existingKeys.add(key));
    return [];
  }

  const incomingTimestamps = executions.map((execution) => Number(execution?.timestamp ?? 0));
  const incomingHasCompleteTimestamps = incomingTimestamps.every((timestamp) => Number.isFinite(timestamp) && timestamp > 0);
  const incomingLastTimestamp = incomingHasCompleteTimestamps ? Math.max(...incomingTimestamps) : 0;
  const openConflict = existingRecords.find((existing) => {
    if (!mayBeSameInstrument(record, existing) || scopesAreDefinitelyDifferent(record, existing) || !isOpenLifecycle(existing)) return false;
    const existingExecutions = readExecutionList(existing);
    const existingTimestamps = existingExecutions.map((execution) => Number(execution?.timestamp ?? 0));
    const existingHasCompleteTimestamps = existingTimestamps.length > 0
      && existingTimestamps.every((timestamp) => Number.isFinite(timestamp) && timestamp > 0);
    const existingFirstTimestamp = existingHasCompleteTimestamps ? Math.min(...existingTimestamps) : 0;
    // Only allow a distinct lifecycle when timestamps prove it fully ended
    // before the existing open position began. Otherwise the relation is
    // ambiguous and must be reviewed rather than guessed.
    return !(incomingHasCompleteTimestamps && existingHasCompleteTimestamps
      && incomingLastTimestamp < existingFirstTimestamp);
  });
  if (openConflict) {
    diagnostics.push(buildOverlapDiagnostic(
      record,
      executions,
      0,
      'OPEN_POSITION_CONTINUATION_REQUIRES_REVIEW',
      'An existing open position in this account and instrument may be continued by these executions. The lifecycle was not saved; review the earlier trade before importing this file.',
    ));
    return [];
  }

  keys.forEach((key) => existingKeys instanceof Map
    ? existingKeys.set(key, (existingKeys.get(key) || 0) + 1)
    : existingKeys.add(key));
  return [record];
}

// Retained for callers that expect a single record. A split lifecycle is
// intentionally not collapsed into one stale aggregate.
export function filterDuplicateExecutionsFromImportedTrade(record: any, existingKeys: Set<string> | Map<string, number>): any | null {
  return filterAndRebuildImportedTrades(record, existingKeys)[0] ?? null;
}
